import { useEffect } from 'react';

const HEAD_DEFAULTS = typeof document !== 'undefined'
  ? {
      title: document.title || 'MicroPinouts',
      description: document.querySelector('meta[name="description"]')?.getAttribute('content') || '',
      keywords: document.querySelector('meta[name="keywords"]')?.getAttribute('content') || '',
      canonical: document.querySelector('link[rel="canonical"]')?.getAttribute('href') || '',
      ogTitle: document.querySelector('meta[property="og:title"]')?.getAttribute('content') || '',
      ogDescription: document.querySelector('meta[property="og:description"]')?.getAttribute('content') || '',
      ogType: document.querySelector('meta[property="og:type"]')?.getAttribute('content') || '',
      ogUrl: document.querySelector('meta[property="og:url"]')?.getAttribute('content') || '',
      ogImage: document.querySelector('meta[property="og:image"]')?.getAttribute('content') || '',
      twitterCard: document.querySelector('meta[name="twitter:card"]')?.getAttribute('content') || '',
      twitterTitle: document.querySelector('meta[name="twitter:title"]')?.getAttribute('content') || '',
      twitterDescription: document.querySelector('meta[name="twitter:description"]')?.getAttribute('content') || '',
      twitterImage: document.querySelector('meta[name="twitter:image"]')?.getAttribute('content') || '',
    }
  : {
      title: 'MicroPinouts',
      description: '',
      keywords: '',
      canonical: '',
      ogTitle: '',
      ogDescription: '',
      ogType: '',
      ogUrl: '',
      ogImage: '',
      twitterCard: '',
      twitterTitle: '',
      twitterDescription: '',
      twitterImage: '',
    };

const ensureMetaTag = (attribute, value) => {
  if (typeof document === 'undefined') {
    return null;
  }

  let tag = document.head.querySelector(`meta[${attribute}="${value}"]`);
  if (!tag) {
    tag = document.createElement('meta');
    tag.setAttribute(attribute, value);
    document.head.appendChild(tag);
  }

  return tag;
};

const ensureCanonicalTag = () => {
  if (typeof document === 'undefined') {
    return null;
  }

  let tag = document.head.querySelector('link[rel="canonical"]');
  if (!tag) {
    tag = document.createElement('link');
    tag.setAttribute('rel', 'canonical');
    document.head.appendChild(tag);
  }

  return tag;
};

const ensureStructuredDataTag = (id) => {
  if (typeof document === 'undefined') {
    return null;
  }

  const normalizedId = String(id || '').trim();
  if (!normalizedId) {
    return null;
  }

  let tag = document.head.querySelector(`script[data-structured-data-id="${normalizedId}"]`);
  if (!tag) {
    tag = document.createElement('script');
    tag.setAttribute('type', 'application/ld+json');
    tag.setAttribute('data-structured-data-id', normalizedId);
    document.head.appendChild(tag);
  }

  return tag;
};

const setMetaContent = (attribute, value, content, fallback = '') => {
  const tag = ensureMetaTag(attribute, value);
  if (!tag) {
    return;
  }

  const normalizedContent = String(content || fallback || '').trim();
  if (normalizedContent) {
    tag.setAttribute('content', normalizedContent);
  } else {
    tag.remove();
  }
};

const setCanonicalHref = (href, fallback = '') => {
  const normalizedHref = String(href || fallback || '').trim();
  const tag = document.head.querySelector('link[rel="canonical"]');

  if (!normalizedHref) {
    if (tag) {
      tag.remove();
    }
    return;
  }

  ensureCanonicalTag()?.setAttribute('href', normalizedHref);
};

export const stripHtmlToText = (value) => String(value || '')
  .replace(/<[^>]+>/g, ' ')
  .replace(/\s+/g, ' ')
  .trim();

const truncateSeoText = (value, maxLength = 160) => {
  const text = stripHtmlToText(value);

  if (!text || text.length <= maxLength) {
    return text;
  }

  const sliced = text.slice(0, Math.max(0, maxLength - 3));
  const trimmed = sliced.replace(/\s+\S*$/, '').replace(/[ ,.;:-]+$/, '');

  return `${trimmed || sliced}...`;
};

const humanizeSeoTerm = (value) => String(value || '')
  .replace(/[-_]+/g, ' ')
  .replace(/\s+/g, ' ')
  .trim();

const normalizeSeoKeywordList = (keywords) => {
  const values = Array.isArray(keywords)
    ? keywords
    : String(keywords || '').split(/[\r\n,;|]+/);

  const seen = new Set();

  return values
    .map((item) => stripHtmlToText(item))
    .filter(Boolean)
    .filter((item) => {
      const key = item.toLowerCase();
      if (seen.has(key)) {
        return false;
      }
      seen.add(key);
      return true;
    })
    .slice(0, 30)
    .join(', ');
};

export const deriveBoardSeoDescription = (board) => {
  const explicitDescription = stripHtmlToText(board?.meta_description);
  if (explicitDescription) {
    return explicitDescription;
  }

  const fromContent = truncateSeoText(board?.description, 160);
  if (fromContent) {
    return fromContent;
  }

  const boardName = String(board?.name || '').trim();
  const manufacturer = String(board?.manufacturer || '').trim();
  const packageType = String(board?.package_type || '').trim();

  let summary = boardName
    ? `Pinout, specs, and documentation for ${boardName}`
    : 'Microcontroller board pinout, specs, and documentation';

  if (manufacturer) {
    summary += ` by ${manufacturer}`;
  }

  if (packageType) {
    summary += ` (${packageType})`;
  }

  return truncateSeoText(`${summary}.`, 160);
};

export const deriveBoardSeoKeywords = (board) => normalizeSeoKeywordList(
  stripHtmlToText(board?.seo_keywords) || [
    board?.name,
    board?.name ? `${board.name} pinout` : '',
    board?.package_type,
    humanizeSeoTerm(board?.category),
    humanizeSeoTerm(board?.board_role),
    'pinout',
    'board pinout',
    'microcontroller pins',
    'electronics reference',
  ]
);

export const deriveWiringGuideSeoDescription = (guide) => {
  const explicitDescription = stripHtmlToText(guide?.meta_description);
  if (explicitDescription) {
    return explicitDescription;
  }

  const fromContent = truncateSeoText(guide?.description, 160);
  if (fromContent) {
    return fromContent;
  }

  const sensorName = String(guide?.sensor_name || '').trim();
  const microcontrollerName = String(guide?.microcontroller_name || '').trim();

  if (sensorName && microcontrollerName) {
    return truncateSeoText(
      `Wiring for connecting ${sensorName} to ${microcontrollerName} with pin mappings and board reference.`,
      160
    );
  }

  return 'Microcontroller wiring with pin mappings and board reference.';
};

export const deriveWiringGuideSeoKeywords = (guide) => normalizeSeoKeywordList(
  stripHtmlToText(guide?.seo_keywords) || [
    guide?.sensor_name,
    guide?.microcontroller_name,
    guide?.sensor_name && guide?.microcontroller_name
      ? `${guide.sensor_name} to ${guide.microcontroller_name}`
      : '',
    guide?.sensor_name ? `${guide.sensor_name} wiring` : '',
    guide?.microcontroller_name ? `${guide.microcontroller_name} wiring` : '',
    'wiring',
    'pin connections',
    'microcontroller wiring',
    'electronics tutorial',
  ]
);

export const deriveGuideSeoDescription = (guide) => {
  const explicitDescription = stripHtmlToText(guide?.meta_description);
  if (explicitDescription) {
    return explicitDescription;
  }

  const fromExcerpt = truncateSeoText(guide?.excerpt, 160);
  if (fromExcerpt) {
    return fromExcerpt;
  }

  const fromContent = truncateSeoText(guide?.content_html, 160);
  if (fromContent) {
    return fromContent;
  }

  const guideTitle = String(guide?.title || '').trim();

  if (guideTitle) {
    return truncateSeoText(`${guideTitle} guide and article from MicroPinouts.`, 160);
  }

  return 'Electronics guides and tutorials from MicroPinouts.';
};

export const deriveGuideSeoKeywords = (guide) => normalizeSeoKeywordList(
  stripHtmlToText(guide?.seo_keywords) || [
    guide?.title,
    guide?.title ? `${guide.title} guide` : '',
    guide?.title ? `${guide.title} tutorial` : '',
    'electronics guide',
    'microcontroller tutorial',
    'maker article',
    'embedded systems guide',
  ]
);

export const usePageSeo = ({
  title,
  description,
  keywords = '',
  canonicalUrl = '',
  ogType = 'article',
  imageUrl = '',
}) => {
  useEffect(() => {
    if (typeof document === 'undefined') {
      return undefined;
    }

    const nextTitle = String(title || HEAD_DEFAULTS.title).trim() || HEAD_DEFAULTS.title;
    const nextDescription = String(description || HEAD_DEFAULTS.description).trim() || HEAD_DEFAULTS.description;
    const nextKeywords = String(keywords || '').trim();
    const nextCanonicalUrl = String(canonicalUrl || '').trim();
    const nextImageUrl = String(imageUrl || '').trim();
    const twitterCardValue = nextImageUrl ? 'summary_large_image' : 'summary';

    document.title = nextTitle;
    setMetaContent('name', 'description', nextDescription, HEAD_DEFAULTS.description);
    setMetaContent('name', 'keywords', nextKeywords, HEAD_DEFAULTS.keywords);
    setMetaContent('property', 'og:title', nextTitle, HEAD_DEFAULTS.ogTitle);
    setMetaContent('property', 'og:description', nextDescription, HEAD_DEFAULTS.ogDescription);
    setMetaContent('property', 'og:type', ogType, HEAD_DEFAULTS.ogType);
    setMetaContent('property', 'og:url', nextCanonicalUrl, HEAD_DEFAULTS.ogUrl);
    setMetaContent('property', 'og:image', nextImageUrl, HEAD_DEFAULTS.ogImage);
    setMetaContent('name', 'twitter:card', twitterCardValue, HEAD_DEFAULTS.twitterCard);
    setMetaContent('name', 'twitter:title', nextTitle, HEAD_DEFAULTS.twitterTitle);
    setMetaContent('name', 'twitter:description', nextDescription, HEAD_DEFAULTS.twitterDescription);
    setMetaContent('name', 'twitter:image', nextImageUrl, HEAD_DEFAULTS.twitterImage);
    setCanonicalHref(nextCanonicalUrl, HEAD_DEFAULTS.canonical);

    return () => {
      document.title = HEAD_DEFAULTS.title;
      setMetaContent('name', 'description', HEAD_DEFAULTS.description);
      setMetaContent('name', 'keywords', HEAD_DEFAULTS.keywords);
      setMetaContent('property', 'og:title', HEAD_DEFAULTS.ogTitle);
      setMetaContent('property', 'og:description', HEAD_DEFAULTS.ogDescription);
      setMetaContent('property', 'og:type', HEAD_DEFAULTS.ogType);
      setMetaContent('property', 'og:url', HEAD_DEFAULTS.ogUrl);
      setMetaContent('property', 'og:image', HEAD_DEFAULTS.ogImage);
      setMetaContent('name', 'twitter:card', HEAD_DEFAULTS.twitterCard);
      setMetaContent('name', 'twitter:title', HEAD_DEFAULTS.twitterTitle);
      setMetaContent('name', 'twitter:description', HEAD_DEFAULTS.twitterDescription);
      setMetaContent('name', 'twitter:image', HEAD_DEFAULTS.twitterImage);
      setCanonicalHref(HEAD_DEFAULTS.canonical);
    };
  }, [canonicalUrl, description, imageUrl, keywords, ogType, title]);
};

export const useStructuredData = (id, data) => {
  useEffect(() => {
    if (typeof document === 'undefined') {
      return undefined;
    }

    const normalizedId = String(id || '').trim();
    const scriptTag = ensureStructuredDataTag(normalizedId);
    const hasStructuredData = Boolean(
      scriptTag
      && data
      && typeof data === 'object'
      && Object.keys(data).length > 0
    );

    if (!scriptTag) {
      return undefined;
    }

    if (!hasStructuredData) {
      scriptTag.remove();
      return undefined;
    }

    scriptTag.textContent = JSON.stringify(data);

    return () => {
      scriptTag.remove();
    };
  }, [data, id]);
};
